import { featuredDoctors, doctorSlug, type Doctor } from "@/lib/healic-data";
import type { DoctorRow } from "@/lib/doctors.functions";

export type MergedDoctor = Doctor & {
  slug: string;
  imageUrl?: string;
  consultFeeInhospital?: number | null;
  consultFeeOnline?: number | null;
  scheduleDays?: string | null;
  scheduleHours?: string | null;
  education?: string[];
  workExperience?: string[];
  metaTitle?: string | null;
  metaDescription?: string | null;
  fromCms?: boolean;
};

export function rowToDoctor(r: DoctorRow): MergedDoctor {
  return {
    name: r.name,
    specialty: r.specialty,
    specialtySlug: r.specialty_slug,
    experience: r.experience,
    hospital: r.hospital ?? undefined,
    locationSlug: r.location_slug ?? undefined,
    gender: r.gender,
    qualifications: r.qualifications ?? undefined,
    languages: r.languages,
    about: r.about ?? undefined,
    slug: r.slug,
    imageUrl: r.image_url ?? undefined,
    consultFeeInhospital: r.consult_fee_inhospital,
    consultFeeOnline: r.consult_fee_online,
    scheduleDays: r.schedule_days,
    scheduleHours: r.schedule_hours,
    education: r.education ?? [],
    workExperience: r.work_experience ?? [],
    metaTitle: r.meta_title,
    metaDescription: r.meta_description,
    fromCms: true,
  };
}

export function staticToMerged(d: Doctor): MergedDoctor {
  return { ...d, slug: doctorSlug(d.name) };
}

/**
 * Merge CMS rows with the static featuredDoctors list, de-duped by slug.
 * CMS entries take precedence over static ones with the same slug.
 */
export function mergeDoctors(rows: DoctorRow[]): MergedDoctor[] {
  const cms = rows.map(rowToDoctor);
  const cmsSlugs = new Set(cms.map((d) => d.slug));
  const statics = featuredDoctors
    .map(staticToMerged)
    .filter((d) => !cmsSlugs.has(d.slug));
  return [...cms, ...statics];
}

export function findMergedDoctor(
  slug: string,
  rows: DoctorRow[] = [],
): MergedDoctor | undefined {
  const cmsHit = rows.find((r) => r.slug === slug);
  if (cmsHit) return rowToDoctor(cmsHit);
  const stat = featuredDoctors.find((d) => doctorSlug(d.name) === slug);
  return stat ? staticToMerged(stat) : undefined;
}
